import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { collectionsService } from "../../../services/collectionsService";
import {
    Button,
    Flex,
    Text,
    useColorModeValue,
    Stack,
    Input,
    FormControl,
    FormErrorMessage,
    FormLabel,
} from "@chakra-ui/react";
import { checkErrors, hasError, isValidVehiclePlate, plateMask } from "../../../utils/forms-utils";
import { validateCpfOrCnpj, formatDocument } from "../../../utils";
import { useNavigate } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { KEYS } from "./../../../constants/keys";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import ConfirmDialog from "../../../components/Dialog/ConfirmDialog";
import YearMaskedInput from "../../../components/YearInput/YearMaskedInput";
import { ROUTES } from "constants/routes-path";

const DarvNew = ({ initialData = null, onSubmit = null, isEdit = false}) => {
    const { t } = useTranslation();
    const showToast = useCustomToast();
    const textColor = useColorModeValue("gray.700", "white");
    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };
    const [formData, setFormData] = useState(undefined);
    const [matResponsavel, setMatResponsavel] = useState("");
    const [inputError, setInputError] = useState({});
    const dialogRef = useRef(undefined);
    const translationKey = "digital-collections";
    const formTranslationKey = `${translationKey}.form`;
    const navigate = useNavigate();

    const ids = useMemo(
            () => ({
                matriculaResponsavel: "registration-number",
                matriculaAuxiliar: "auxiliar-registration",
                localDarv: "darv-place",
                parecer: "legal-opinion",
                observacoes: "observations",
                placaVeiculo: "vehicle-plate",
                ufPlaca: "uf-plate",
                renavamVeiculo: "vehicle-renavam",
                modeloVeiculo: "vehicle-model",
                corVeiculo: "vehicle-color",
                anoFabricacaoVeiculo: "vehicle-fabrication-year",
                chassiVeiculo: "vehicle-chassis",
                nomeProprietarioVeiculo: "vehicle-owner-name",
                cpfCnpjProprietarioVeiculo: "vehicle-owner-cpf-cnpj",
                motivoRemocao: "remotion-reason",
                servicoUtilizado: "utilized-service",
                responsavelRemocao: "remotion-responsible",
                equipamentoFaltante: "lacking-equipment",
                informacoesComplementares: "complementary-info"
            }),
            []
        );

    const initFormData = () => {
        return {
            [ids.matriculaAuxiliar]: {
                value: initialData?.matriculaAuxiliar || "",
                validations: [],
            },
            [ids.localDarv]: {
                value: initialData?.localDarv || "",
                validations: [{ required: true }],
            },
            [ids.parecer]: {
                value: initialData?.parecer || "",
                validations: [],
            },
            [ids.observacoes]: {
                value: initialData?.observacoes || "",
                validations: [],
            },
            [ids.placaVeiculo]: {
                value: initialData?.placaVeiculo || "",
                validations: [],
            },
            [ids.ufPlaca]: {
                value: initialData?.ufPlaca || "",
                validations: [],
            },
            [ids.renavamVeiculo]: {
                value: initialData?.renavamVeiculo || "",
                validations: [],
            },
            [ids.modeloVeiculo]: {
                value: initialData?.modeloVeiculo || "",
                validations: [],
            },
            [ids.corVeiculo]: {
                value: initialData?.corVeiculo || "",
                validations: [],
            },
            [ids.anoFabricacaoVeiculo]: {
                value: initialData?.anoFabricacaoVeiculo || "",
                validations: [],
            },
            [ids.chassiVeiculo]: {
                value: initialData?.chassiVeiculo || "",
                validations: [],
            },
            [ids.nomeProprietarioVeiculo]: {
                value: initialData?.nomeProprietarioVeiculo || "",
                validations: [],
            },
            [ids.cpfCnpjProprietarioVeiculo]: {
                value: initialData?.cpfCnpjProprietarioVeiculo || "",
                validations: [],
            },
            [ids.motivoRemocao]: {
                value: initialData?.motivoRemocao || "",
                validations: [{ required: true }],
            },
            [ids.servicoUtilizado]: {
                value: initialData?.servicoUtilizado || "",
                validations: [{ required: true }],
            },
            [ids.responsavelRemocao]: {
                value: initialData?.responsavelRemocao || "",
                validations: [{ required: true }],
            },
            [ids.equipamentoFaltante]: {
                value: initialData?.equipamentoFaltante || "",
                validations: [],
            },
            [ids.informacoesComplementares]: {
                value: initialData?.informacoesComplementares || "",
                validations: [],
            }
        };
    };

    const maskMap = {
        [ids.placaVeiculo]: plateMask,
        [ids.cpfCnpjProprietarioVeiculo]: formatDocument,
    };

    const validatorMap = {
        [ids.placaVeiculo]: {
            validate: (v) => !v || isValidVehiclePlate(v),
            errorKey: 'vehicle-plate-format',
        },
        [ids.cpfCnpjProprietarioVeiculo]: {
            validate: validateCpfOrCnpj,
            errorKey: 'cpf-format',
        }
    };
    
    const handleInputChange = (e) => {
        const { id, value } = e.target;
        
        const maskedValue = maskMap[id] ? maskMap[id](value) : value;
        
        const newFormData = {
            ...formData,
            [id]: { ...formData[id], value: maskedValue },
        };
        
        setFormData(newFormData);

        const validator = validatorMap[id];
        let formValidationErrors = checkErrors({ formData: newFormData, inputError: inputError, name: id })

        if (validator) {
            if (!validator.validate(maskedValue)) {
                formValidationErrors[id] = t(`${translationKey}.messages.errors.${validator.errorKey}`);
            } else {
                delete formValidationErrors[id];
            }
        }

        setInputError(formValidationErrors);
    };

    const isFormInvalid = () => {
         return formData && inputError ? hasError({ formData, inputError }) : true;
    };

    const toFormData = () => {
        const formValues = {
            mat_responsavel: matResponsavel,
            mat_auxiliar: formData[ids.matriculaAuxiliar].value.trim(),
            local: formData[ids.localDarv].value.trim(),
            parecer: formData[ids.parecer].value.trim(),
            observacoes: formData[ids.observacoes].value.trim(),
            placa: formData[ids.placaVeiculo].value.trim(),
            uf: formData[ids.ufPlaca].value.trim(),
            renavam: formData[ids.renavamVeiculo].value.trim(),
            chassi: formData[ids.ufPlaca].value.trim(),
            marca_modelo: formData[ids.modeloVeiculo].value.trim(),
            ano_fabricacao: formData[ids.anoFabricacaoVeiculo].value.trim(),
            cor: formData[ids.corVeiculo].value.trim(),
            nome_proprietario: formData[ids.nomeProprietarioVeiculo].value.trim(),
            cpf_cnpj_proprietario: formData[ids.cpfCnpjProprietarioVeiculo].value.trim(),
            motivo_remocao: formData[ids.motivoRemocao].value.trim(),
            servico_utilizado: formData[ids.servicoUtilizado].value.trim(),
            responsavel_remocao: formData[ids.responsavelRemocao].value.trim(),
            equip_ausente_ineficiente: formData[ids.equipamentoFaltante].value.trim(),
            info_complementares: formData[ids.informacoesComplementares].value.trim(),
        };

        return ("darvGeneric", new Blob([JSON.stringify(formValues)], { type: "application/json" }));;
    };

    const handleSave = async () => {
        try {
            const data = toFormData();
            if (isEdit && onSubmit) {
                await onSubmit(data);
            } else {
                const { responseData, headers } = await collectionsService.create(data);

                const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

                const url = URL.createObjectURL(new Blob([responseData]));
                Object.assign(document.createElement('a'), {
                    href: url,
                    download: fileName
                }).click();

                URL.revokeObjectURL(url);
                showToast({
                    title: t("digital-collections.messages.success.create"),
                    status: "success",
                });
                redirectToList();
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const redirectToList = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.digitalCollections);
    };

    useEffect(() => {
        setFormData(initFormData())
        const userRaw = localStorage.getItem(KEYS.user);
        if (userRaw) {
            const user = JSON.parse(userRaw);
            setMatResponsavel(user.agente_matricula);
        }
    }, []);

    return (
        <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>

            {formData && matResponsavel && (
                <>
                <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                    <CardHeader mb="20px">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${formTranslationKey}.title.document-data`)}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={inputError[ids.matriculaResponsavel]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.matriculaResponsavel}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.matriculaResponsavel}
                                        name={ids.matriculaResponsavel}
                                        disabled={true}
                                        value={matResponsavel}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.matriculaResponsavel}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.matriculaResponsavel])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.matriculaAuxiliar]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.matriculaAuxiliar}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.matriculaAuxiliar}
                                        name={ids.matriculaAuxiliar}
                                        value={formData[ids.matriculaAuxiliar].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.matriculaAuxiliar}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.matriculaAuxiliar])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isRequired isInvalid={inputError[ids.localDarv]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.localDarv}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.localDarv}
                                        name={ids.localDarv}
                                        value={formData[ids.localDarv].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.localDarv}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.localDarv])}
                                    </FormErrorMessage>
                                </FormControl>

                            </Stack>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.parecer]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.parecer}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.parecer}
                                        name={ids.parecer}
                                        value={formData[ids.parecer].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.parecer}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.parecer])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.observacoes]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.observacoes}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.observacoes}
                                        name={ids.observacoes}
                                        value={formData[ids.observacoes].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.observacoes}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.observacoes])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>
                        </Stack>
                    </CardBody>
                </Card>
                <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                    <CardHeader mb="20px">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${formTranslationKey}.title.vehicle-data`)}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.placaVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.placaVeiculo}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.placaVeiculo}
                                        name={ids.placaVeiculo}
                                        value={formData[ids.placaVeiculo].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.placaVeiculo}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.placaVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.ufPlaca]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.ufPlaca}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.ufPlaca}
                                        name={ids.ufPlaca}
                                        maxLength={2}
                                        value={formData[ids.ufPlaca].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.ufPlaca}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.ufPlaca])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.renavamVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.renavamVeiculo}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.renavamVeiculo}
                                        name={ids.renavamVeiculo}
                                        value={formData[ids.renavamVeiculo].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.renavamVeiculo}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.renavamVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.modeloVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.modeloVeiculo}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.modeloVeiculo}
                                        name={ids.modeloVeiculo}
                                        value={formData[ids.modeloVeiculo].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.modeloVeiculo}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.modeloVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.corVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.corVeiculo}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.corVeiculo}
                                        name={ids.corVeiculo}
                                        value={formData[ids.corVeiculo].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.corVeiculo}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.corVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>

                                <YearMaskedInput
                                    id={ids.anoFabricacaoVeiculo}
                                    label={`${formTranslationKey}.fields.${ids.anoFabricacaoVeiculo}.label`}
                                    placeholder={`${formTranslationKey}.fields.${ids.anoFabricacaoVeiculo}.placeholder`}
                                    value={formData[ids.anoFabricacaoVeiculo].value}
                                    onChange={handleInputChange}
                                    error={inputError[ids.anoFabricacaoVeiculo]}
                                />
                            </Stack>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.chassiVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.chassiVeiculo}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.chassiVeiculo}
                                        name={ids.chassiVeiculo}
                                        value={formData[ids.chassiVeiculo].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.chassiVeiculo}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.chassiVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.nomeProprietarioVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.nomeProprietarioVeiculo}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.nomeProprietarioVeiculo}
                                        name={ids.nomeProprietarioVeiculo}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.nomeProprietarioVeiculo}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.nomeProprietarioVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.cpfCnpjProprietarioVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.${ids.cpfCnpjProprietarioVeiculo}.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.cpfCnpjProprietarioVeiculo}
                                        name={ids.cpfCnpjProprietarioVeiculo}
                                        value={formData[ids.cpfCnpjProprietarioVeiculo].value}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.${ids.cpfCnpjProprietarioVeiculo}.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.cpfCnpjProprietarioVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>
                        </Stack>
                    </CardBody>
                </Card>
                <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                    <CardHeader mb="20px">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${formTranslationKey}.title.remotion-data`)}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Stack w="100%" direction="column" spacing={formSpacing}>
                            <FormControl isRequired isInvalid={inputError[ids.motivoRemocao]}>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t(`${formTranslationKey}.fields.${ids.motivoRemocao}.label`)}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    id={ids.motivoRemocao}
                                    name={ids.motivoRemocao}
                                    value={formData[ids.motivoRemocao].value}
                                    onChange={handleInputChange}
                                    placeholder={t(`${formTranslationKey}.fields.${ids.motivoRemocao}.placeholder`)}
                                />
                                <FormErrorMessage>
                                    {t(inputError[ids.motivoRemocao])}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isRequired isInvalid={inputError[ids.servicoUtilizado]}>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t(`${formTranslationKey}.fields.${ids.servicoUtilizado}.label`)}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    id={ids.servicoUtilizado}
                                    name={ids.servicoUtilizado}
                                    value={formData[ids.servicoUtilizado].value}
                                    onChange={handleInputChange}
                                    placeholder={t(`${formTranslationKey}.fields.${ids.servicoUtilizado}.placeholder`)}
                                />
                                <FormErrorMessage>
                                    {t(inputError[ids.servicoUtilizado])}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isRequired isInvalid={inputError[ids.responsavelRemocao]}>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t(`${formTranslationKey}.fields.${ids.responsavelRemocao}.label`)}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    id={ids.responsavelRemocao}
                                    name={ids.responsavelRemocao}
                                    value={formData[ids.responsavelRemocao].value}
                                    onChange={handleInputChange}
                                    placeholder={t(`${formTranslationKey}.fields.${ids.responsavelRemocao}.placeholder`)}
                                />
                                <FormErrorMessage>
                                    {t(inputError[ids.responsavelRemocao])}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={inputError[ids.equipamentoFaltante]}>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t(`${formTranslationKey}.fields.${ids.equipamentoFaltante}.label`)}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    id={ids.equipamentoFaltante}
                                    name={ids.equipamentoFaltante}
                                    value={formData[ids.equipamentoFaltante].value}
                                    onChange={handleInputChange}
                                    placeholder={t(`${formTranslationKey}.fields.${ids.equipamentoFaltante}.placeholder`)}
                                />
                                <FormErrorMessage>
                                    {t(inputError[ids.equipamentoFaltante])}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={inputError[ids.informacoesComplementares]}>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t(`${formTranslationKey}.fields.${ids.informacoesComplementares}.label`)}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    id={ids.informacoesComplementares}
                                    name={ids.informacoesComplementares}
                                    value={formData[ids.informacoesComplementares].value}
                                    onChange={handleInputChange}
                                    placeholder={t(`${formTranslationKey}.fields.${ids.informacoesComplementares}.placeholder`)}
                                />
                                <FormErrorMessage>
                                    {t(inputError[ids.informacoesComplementares])}
                                </FormErrorMessage>
                            </FormControl>
                        </Stack>

                    </CardBody>
                </Card>
                <Stack w="100%" direction="row" justify="space-between">
                    <Button 
                        w="125px" 
                        variant="no-effects" 
                        bg="gray.100" 
                        color="gray.700" 
                        fontSize="sm" 
                        fontWeight="bold" 
                        onClick={redirectToList}>
                        <Text>
                            {t("system.actions.cancel")}
                        </Text>
                    </Button>

                    <Button
                        w="125px"
                        variant="dark"
                        fontSize="sm"
                        isDisabled={isFormInvalid()}
                        onClick={handleSave}
                        _hover={isFormInvalid() ? {} : undefined}
                    >
                        <Text>
                            {t("system.actions.save")}
                        </Text>
                    </Button>
                </Stack>
                </>
            )}
            <ConfirmDialog ref={dialogRef} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
};

export default DarvNew;
